import styles from "@src/components/breadcrumbs/BreadCrumbs.module.scss";
import { Typography } from "top-brains-ui";
import { Link, useLocation } from "react-router-dom";
import homeIcon from "@src/assets/homeIcon.svg";
import { BREADCRUMB_COLOR_CONSTANTS } from "@src/styles/ColorConstants";
import { USER_CONSTANTS } from "@src/constants/UiConstants";
import { useSelector } from "react-redux";
import { RootState } from "@src/app/store";
import { LevelInput, mapLevel } from "@src/utils/CommonUtils";

type OrgType = Extract<LevelInput, "ORG" | "SUB_ORG" | "CLIENT" | "BRANCH">;

const BreadCrumbs = () => {
  const location = useLocation();
  const pathnames = location.pathname.split("/").filter((x) => x);

  const userRole = useSelector(
    (state: RootState) => state.globalSlice.userDetails?.currentPrivilege,
  );

  const organizationType: OrgType = useSelector(
    (state: RootState) => state.globalSlice.userDetails?.organizationType,
  );

  const formatBreadcrumb = (text: string): string => {
    return decodeURIComponent(text)
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getOrganizationsHomePath = (pathname: string): string => {
    const segments = pathname.split("/").filter(Boolean);

    if (segments.length === 1) return "/organizations";

    if (segments[1] === "sub-organization")
      return "/organizations/sub-organization";

    if (segments[1] === "clients") return "/organizations/clients";

    if (segments[1] === "branch") return "/organizations/branch";

    if (segments[2] === "clients")
      return "/organizations/sub-organization/clients";

    if (segments[3] === "branch")
      return "/organizations/sub-organization/clients/branch";

    return "/organizations";
  };

  const isBreadcrumbClickable = (segment: string, index: number) => {
    if (userRole !== USER_CONSTANTS.ADMIN) return true;

    if (mapLevel(organizationType) === "ORG") {
      if (index === 0) return false;
    }

    if (mapLevel(organizationType) === "SUB_ORG") {
      if (index === 0 || segment === "sub-organization") return false;
    }

    if (mapLevel(organizationType) === "CLIENT") {
      if (
        index === 0 ||
        segment === "sub-organization" ||
        segment === "clients"
      )
        return false;
    }
    if (mapLevel(organizationType) === "BRANCH") {
      if (
        index === 0 ||
        segment === "sub-organization" ||
        segment === "clients" ||
        segment === "branch"
      )
        return false;
    }

    return true;
  };

  const firstSegment = pathnames[0];
  let homePath = `/${firstSegment}`;

  if (firstSegment === "activities") {
    homePath = "/activities/myQuiz";
  }

  if (firstSegment === "organizations" && userRole === USER_CONSTANTS.ADMIN) {
    homePath = getOrganizationsHomePath(location.pathname) ?? "/organizations";
  }

  if (pathnames[pathnames.length - 2] === "quiz") return <></>;

  return (
    <nav aria-label="breadcrumb">
      <ul className={styles.breadcrumbList}>
        {pathnames.length > 0 && (
          <li className={styles.breadcrumbItem}>
            <div className={styles.chipSection}>
              {isBreadcrumbClickable(pathnames[0], 0) ? (
                <Link to={homePath} className={styles.breadcrumbLink}>
                  <Typography
                    fontSize="10px"
                    fontWeight="regular"
                    className={styles.homeContainer}
                  >
                    <img src={homeIcon} alt="home" />{" "}
                    {formatBreadcrumb(pathnames[0])}
                  </Typography>
                </Link>
              ) : (
                <Typography
                  fontSize="10px"
                  fontWeight="regular"
                  className={styles.homeContainer}
                  style={{ cursor: "not-allowed", opacity: 0.6 }}
                >
                  <img src={homeIcon} alt="home" />{" "}
                  {formatBreadcrumb(pathnames[0])}
                </Typography>
              )}
            </div>

            {pathnames.length > 1 && (
              <Typography
                fontSize="10px"
                fontWeight="bold"
                color={BREADCRUMB_COLOR_CONSTANTS.SEPARATOR_COLOR}
              >
                /
              </Typography>
            )}
          </li>
        )}
        {pathnames.slice(1).map((value, index) => {
          const to = `/${pathnames.slice(0, index + 2).join("/")}`;
          const isLast = index === pathnames.slice(1).length - 1;
          const clickable = isBreadcrumbClickable(value, index + 1);

          return (
            <li key={to} className={styles.breadcrumbItem}>
              <div className={styles.chipSection}>
                {isLast || !clickable ? (
                  <Typography
                    fontSize="10px"
                    fontWeight="regular"
                    color={BREADCRUMB_COLOR_CONSTANTS.TEXT_COLOR}
                    style={{
                      cursor: clickable ? "default" : "not-allowed",
                      opacity: clickable ? 1 : 0.6,
                    }}
                  >
                    {formatBreadcrumb(value)}
                  </Typography>
                ) : (
                  <Link to={to} className={styles.breadcrumbLink}>
                    <Typography
                      fontSize="10px"
                      fontWeight="regular"
                      color={BREADCRUMB_COLOR_CONSTANTS.TEXT_COLOR}
                    >
                      {formatBreadcrumb(value)}
                    </Typography>
                  </Link>
                )}
              </div>
              {!isLast && (
                <Typography
                  fontSize="10px"
                  fontWeight="bold"
                  color={BREADCRUMB_COLOR_CONSTANTS.SEPARATOR_COLOR}
                >
                  /
                </Typography>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default BreadCrumbs;
